import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUpFromBracket,
  faCopy,
  faCheck,
  faEllipsis,
} from '@fortawesome/free-solid-svg-icons'
import useClickAway from 'react-use/lib/useClickAway'
import useKey from 'react-use/lib/useKey'
import ChatDropdown from './chatDropdown'
import ConfirmModal from '../confirmModal'
import { truncatePhrase } from '~/prix'
import { useLlmChatContext } from '~/components/llm/llmChatContext'
import { ClipLoader } from 'react-spinners'
import { Helmet as HelmetBase } from 'react-helmet'

const Helmet = HelmetBase as unknown as React.ComponentType<any>

const Wrapper = styled.div`
  position: sticky;
  left: 0px;
  top: 0px;
  right: 0px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px 10px 40px;
  background: #ebebeb;
  background: linear-gradient(0deg, rgba(235, 235, 235, 0) 0%, rgba(235, 235, 235, 1) 65%);
  z-index: 2;
`

const TitleText = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  text-align: center;

  @media screen and (max-width: 1150px) {
    margin-top: 20px;
  }
`

const WrapperButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: absolute;
  right: 5px;
  top: 5px;

  @media screen and (max-width: 1080px) {
    gap: 15px;
    right: 10px;
  }
`

const TitleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.darkGrey};
  display: flex;
  align-items: center;
  font-size: 14px;
  display: flex;
  gap: 8px;
  padding: 5px 10px;
  border-radius: 5px;
  transition: all 0.25s;
  font-weight: 600;

  > svg {
    font-size: 18px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }

  @media screen and (max-width: 1080px) {
    font-size: 11px;
    max-width: 120px;
    padding: 5px 0;

    > svg {
      font-size: 16px;
    }
  }
`

const ShareTooltip = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 40px;
  right: 10px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 12px;
  min-width: 300px;
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  visibility: ${props => (props.$isVisible ? 'visible' : 'hidden')};
  transform: ${props => (props.$isVisible ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.2s ease;

  &:before {
    content: '';
    position: absolute;
    top: -10px;
    right: 80px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid white;

    @media screen and (max-width: 1080px) {
      right: 40px;
    }
  }

  @media screen and (max-width: 1080px) {
    top: 30px;
  }
`

const TooltipTitle = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.darkGrey};
  margin-bottom: 8px;
  font-weight: 600;
`

const InputContainer = styled.div`
  display: flex;
  gap: 8px;
`

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.darkGrey};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const CopyButton = styled.button<{ $copied: boolean }>`
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  background: ${props => (props.$copied ? props.theme.colors.success : props.theme.colors.primary)};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }
`

interface ChatTitleProps {
  children: React.ReactNode
  chatId: number
  isFixed: boolean
  isArchived?: boolean
}

const ChatTitle: React.FC<ChatTitleProps> = ({ children, chatId, isFixed, isArchived = false }) => {
  const { onArchiveChat, onFixChat, onRestoreChat } = useLlmChatContext()
  const [isLoadingShare, setIsLoadingShare] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [copied, setCopied] = useState(false)
  const [shareLink, setShareLink] = useState('')
  const tooltipRef = useRef(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLButtonElement>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoadingFix, setIsLoadingFix] = useState(false)

  useClickAway(tooltipRef, () => {
    if (showTooltip) {
      setShowTooltip(false)
    }
  })

  useKey('Escape', () => {
    if (showTooltip) {
      setShowTooltip(false)
    }
  })

  const handleShare = async () => {
    setIsLoadingShare(true)
    // Simular geração do link de compartilhamento
    await new Promise(resolve => setTimeout(resolve, 1000))
    setShareLink('https://prix.gov/chat/123456')
    setIsLoadingShare(false)
    setShowTooltip(true)
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(shareLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDropdown = () => {
    setIsDropdownOpen(prev => !prev)
  }

  const handleArchive = async () => {
    setIsDropdownOpen(false)
    setIsModalOpen(true)
  }

  const handleConfirmArchive = async () => {
    const result = await onArchiveChat(chatId)
    if (result) {
      setIsModalOpen(false)
    }
  }

  const handleRestore = async () => {
    setIsDropdownOpen(false)
    setIsLoadingFix(true)
    const result = await onRestoreChat(chatId)
    setIsLoadingFix(false)
  }

  const handleFix = async () => {
    setIsLoadingFix(true)
    setIsDropdownOpen(false)
    await onFixChat(chatId, !isFixed)
    setIsLoadingFix(false)
  }

  return (
    <>
      <Wrapper>
        <TitleText>
          {typeof children === 'string' ? truncatePhrase(children, 10) : children}
        </TitleText>

        {chatId >= 0 && (
          <WrapperButtons>
            {/* <TitleButton onClick={handleShare} style={{ minWidth: 85 }}>
            {isLoadingShare ? (
              <ClipLoader size={16} color='currentColor' speedMultiplier={0.7} />
            ) : (
              <>
                <FontAwesomeIcon icon={faArrowUpFromBracket} />
                Compartilhar
              </>
            )}
          </TitleButton>
          <ShareTooltip ref={tooltipRef} $isVisible={showTooltip}>
            <TooltipTitle>Link para compartilhamento</TooltipTitle>
            <InputContainer>
              <Input value={shareLink} readOnly onClick={e => e.stopPropagation()} />
              <CopyButton onClick={handleCopy} $copied={copied}>
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                {copied ? 'Copiado' : 'Copiar'}
              </CopyButton>
            </InputContainer>
          </ShareTooltip> */}

            <TitleButton onClick={handleDropdown} ref={dropdownRef}>
              {isLoadingFix ? (
                <ClipLoader size={16} color='currentColor' speedMultiplier={0.7} />
              ) : (
                <FontAwesomeIcon icon={faEllipsis} />
              )}
            </TitleButton>
          </WrapperButtons>
        )}
      </Wrapper>

      {!isArchived ? (
        <ChatDropdown
          isOpen={isDropdownOpen}
          isFixed={isFixed}
          onClose={() => setIsDropdownOpen(false)}
          onFix={handleFix}
          onArchive={handleArchive}
          anchorElement={dropdownRef.current}
        />
      ) : (
        <ChatDropdown
          isOpen={isDropdownOpen}
          isFixed={false}
          onClose={() => setIsDropdownOpen(false)}
          onRestore={handleRestore}
          anchorElement={dropdownRef.current}
        />
      )}

      {isModalOpen && (
        <ConfirmModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={() => handleConfirmArchive()}
          title='Arquivar chat?'
          description='Tem certeza que deseja arquivar esse chat?'
        />
      )}
    </>
  )
}

export default ChatTitle
